import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Container, Typography, Card, Stack, Button, 
  Chip, Avatar, Paper, Pagination, Fade, Dialog, 
  DialogTitle, DialogContent, IconButton,
  CircularProgress, Grid, Divider, Skeleton
} from '@mui/material';
import { 
  AccountBalanceWallet, ShoppingCart, ArrowBack, 
  ReceiptLong, FilterList, Close, CheckCircle, 
  Schedule, Cancel, Info, MonetizationOn, CardGiftcard,
  CalendarToday
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';

// --- Premium Theme Config ---
const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
const darkGradient = 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)';
const lightBg = '#F8FAFC';

export default function PurchaseHistory() {
  const { fetchTransactions, loading } = useWallet();
  
  // --- States ---
  const [apiData, setApiData] = useState(null); 
  const [currentFilter, setCurrentFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  // --- Data Loading Logic ---
  const loadData = useCallback(async (filter, p) => {
    try {
      const response = await fetchTransactions({ 
        type: 'purchase', 
        status: filter === 'all' ? undefined : filter,
        page: p 
      });
      if (response.success) {
        setApiData(response);
      }
    } catch (err) {
      console.error("Error loading purchases:", err);
    }
  }, [fetchTransactions]);

  useEffect(() => {
    loadData(currentFilter, page);
  }, [currentFilter, page, loadData]);

  // --- Handlers ---
  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString, formatType) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (formatType === 'full') {
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedPurchase(null), 300);
  };

  const getStatusMeta = (status) => {
    const s = status?.toLowerCase();
    switch(s) {
      case 'completed': case 'success': return { icon: <CheckCircle />, color: '#10B981', bg: '#ECFDF5', label: 'Completed' };
      case 'pending': return { icon: <Schedule />, color: '#F59E0B', bg: '#FFFBEB', label: 'Pending' };
      case 'failed': return { icon: <Cancel />, color: '#EF4444', bg: '#FEF2F2', label: 'Failed' };
      default: return { icon: <Info />, color: '#64748B', bg: '#F1F5F9', label: status };
    }
  };

  // API डेटा को वेरिएबल्स में मैप करना
  const wallet = apiData?.wallet;
  const purchases = apiData?.transactions || [];
  const pagination = apiData?.pagination;

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 12 }}>
      
      {/* 🌟 HERO SECTION 🌟 */}
      <Box sx={{ pt: { xs: 8, md: 10 }, pb: { xs: 10, md: 12 }, background: darkGradient, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '3px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(238, 9, 121, 0.4)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <ReceiptLong sx={{ fontSize: { xs: 50, md: 60 }, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h3" fontWeight="900" color="white" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1 }}>
            खरीदारी इतिहास / Purchase History
          </Typography>
          <Typography variant="body1" color="#94A3B8" fontWeight="500">
            View details of all your credit purchases
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: -5, md: -6 }, position: 'relative', zIndex: 2 }}>
        
        {/* 🌟 WALLET SUMMARY (Compact Row) 🌟 */}
        <Card sx={{ p: 0, borderRadius: 4, boxShadow: '0 8px 25px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0', mb: 4, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'white', textAlign: 'center', borderRight: '1px solid #E2E8F0' }}>
              <Avatar sx={{ bgcolor: '#EFF6FF', color: '#3B82F6', width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, mx: 'auto', mb: { xs: 1, md: 2 } }}>
                <AccountBalanceWallet sx={{ fontSize: { xs: 20, md: 30 } }} />
              </Avatar>
              <Typography variant="h3" fontWeight="900" color="#3B82F6" sx={{ fontSize: { xs: '1.4rem', md: '2.5rem' }, lineHeight: 1 }}>
                {loading && !wallet ? '...' : (wallet?.balance ?? 0)}
              </Typography>
              <Typography variant="caption" color="#64748B" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: 'block' }}>Balance</Typography>
            </Box>
            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'white', textAlign: 'center', borderRight: '1px solid #E2E8F0' }}>
              <Avatar sx={{ bgcolor: '#ECFDF5', color: '#10B981', width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, mx: 'auto', mb: { xs: 1, md: 2 } }}>
                <ShoppingCart sx={{ fontSize: { xs: 20, md: 30 } }} />
              </Avatar>
              <Typography variant="h3" fontWeight="900" color="#10B981" sx={{ fontSize: { xs: '1.4rem', md: '2.5rem' }, lineHeight: 1 }}>
                {loading && !pagination ? '...' : (pagination?.total ?? 0)}
              </Typography>
              <Typography variant="caption" color="#64748B" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: 'block' }}>Purchases</Typography>
            </Box>
            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'white', textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#FFFBEB', color: '#F59E0B', width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, mx: 'auto', mb: { xs: 1, md: 2 } }}>
                <MonetizationOn sx={{ fontSize: { xs: 20, md: 30 } }} />
              </Avatar>
              <Typography variant="h3" fontWeight="900" color="#F59E0B" sx={{ fontSize: { xs: '1.4rem', md: '2.5rem' }, lineHeight: 1 }}>
                ₹{loading && !wallet ? '...' : (wallet?.total_spent ?? 0)}
              </Typography>
              <Typography variant="caption" color="#64748B" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: 'block' }}>Spent</Typography>
            </Box>
          </Box>
        </Card>

        {/* 🌟 FILTERS & ACTIONS (FIXED BUTTONS) 🌟 */}
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 3, boxShadow: 'none', border: '1px solid #E2E8F0', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' }, overflowX: 'auto', '::-webkit-scrollbar': { display: 'none' } }}>
            <Typography variant="body2" fontWeight="700" color="#1E293B" sx={{ display: 'flex', alignItems: 'center', minWidth: 'max-content' }}>
              <FilterList sx={{ fontSize: 18, mr: 0.5 }} /> Filter:
            </Typography>
            <Stack direction="row" spacing={1}>
              {[{ val: 'all', label: 'All' }, { val: 'completed', label: 'Completed' }, { val: 'pending', label: 'Pending' }, { val: 'failed', label: 'Failed' }].map((opt) => (
                <Chip 
                  key={opt.val} label={opt.label} onClick={() => handleFilterChange(opt.val)}
                  sx={{ fontWeight: 'bold', cursor: 'pointer', bgcolor: currentFilter === opt.val ? '#1E293B' : '#F1F5F9', color: currentFilter === opt.val ? 'white' : '#475569', '&:hover': { bgcolor: currentFilter === opt.val ? '#0F172A' : '#E2E8F0' } }}
                />
              ))}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5} width={{ xs: '100%', md: 'auto' }}>
            <Button component={Link} to="/my-profile" variant="outlined" startIcon={<ArrowBack />} sx={{ flex: 1, borderRadius: 50, color: '#475569', borderColor: '#CBD5E1', fontWeight: 'bold', whiteSpace: 'nowrap', px: { xs: 1.5, md: 3 } }}>Back</Button>
            <Button component={Link} to="/plans" variant="contained" startIcon={<ShoppingCart />} sx={{ flex: 1, background: primaryGradient, borderRadius: 50, boxShadow: 'none', fontWeight: 'bold', whiteSpace: 'nowrap', px: { xs: 1.5, md: 3 } }}>Buy Credits</Button>
          </Stack>
        </Card>

        <Typography variant="h6" fontWeight="800" color="#1E293B" mb={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <ReceiptLong sx={{ mr: 1, color: '#FF6A00' }} /> Recent Purchases
        </Typography>

        {/* 🌟 PURCHASES CARDS LIST 🌟 */}
        {loading && !apiData ? (
          <Stack spacing={2}>{ [1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={150} sx={{ borderRadius: 4 }} />) }</Stack>
        ) : purchases.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1px dashed #CBD5E1', bgcolor: 'transparent' }}>
            <ShoppingCart sx={{ fontSize: 60, color: '#94A3B8', mb: 2 }} />
            <Typography variant="h6" fontWeight="800" color="#1E293B">No Purchases Found</Typography>
          </Paper>
        ) : (
          <Stack spacing={2.5} mb={4}>
            {purchases.map((txn, index) => {
              const meta = getStatusMeta(txn.status);
              return (
                <Fade in={true} timeout={(index + 1) * 300} key={txn.id}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: '1px solid #E2E8F0', bgcolor: 'white', transition: 'all 0.2s', '&:hover': { borderColor: meta.color, boxShadow: `0 8px 25px ${meta.color}15`, transform: 'translateY(-2px)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: meta.bg, color: meta.color, width: 48, height: 48 }}>{meta.icon}</Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="800" color="#1E293B" lineHeight={1.2}>{txn.plan_name || txn.remark || 'Credit Purchase'}</Typography>
                          <Typography variant="caption" color="#64748B" fontWeight="600">{formatDate(txn.created_at, 'full')}</Typography>
                        </Box>
                      </Box>
                      <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 'bold', borderRadius: 2 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip icon={<MonetizationOn sx={{ fontSize: 16 }} />} label={`${txn.amount} Credits`} size="small" sx={{ bgcolor: '#EFF6FF', color: '#3B82F6', fontWeight: '700', borderRadius: 2 }} />
                      {txn.bonus_credits > 0 && <Chip icon={<CardGiftcard sx={{ fontSize: 16 }} />} label={`+${txn.bonus_credits} Bonus`} size="small" sx={{ bgcolor: '#ECFDF5', color: '#10B981', fontWeight: '700', borderRadius: 2 }} />}
                      <Chip label={`Ref: ${txn.reference_id || 'N/A'}`} size="small" variant="outlined" sx={{ color: '#94A3B8', borderColor: '#E2E8F0', fontWeight: '600', borderRadius: 2 }} />
                    </Box>
                    <Divider sx={{ mb: 2, borderStyle: 'dashed', borderColor: '#E2E8F0' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box><Typography variant="caption" color="#94A3B8" fontWeight="600" display="block" mb={-0.5}>Amount Paid</Typography><Typography variant="h5" fontWeight="900" color="#1E293B">₹{txn.amount_paid || txn.amount}</Typography></Box>
                      <Button variant="outlined" size="small" onClick={() => { setSelectedPurchase(txn); setModalOpen(true); }} sx={{ borderRadius: 50, fontWeight: 'bold', color: '#3B82F6', borderColor: '#BFDBFE', px: 2 }}>View Details</Button>
                    </Box>
                  </Paper>
                </Fade>
              );
            })}
          </Stack>
        )}

        {pagination?.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={pagination.totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded" />
          </Box>
        )}
      </Container>

      {/* 🌟 PURCHASE DETAILS MODAL 🌟 */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, pt: 3 }}>
          <Typography variant="h6" fontWeight="800" display="flex" alignItems="center"><ReceiptLong sx={{ mr: 1, color: '#FF6A00' }} /> Purchase Details</Typography>
          <IconButton onClick={handleCloseModal} size="small" sx={{ bgcolor: '#F1F5F9' }}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {selectedPurchase && (
            <Box>
              <Box textAlign="center" mb={4}>
                <Typography variant="h5" fontWeight="900" color="#1E293B" mb={1}>{selectedPurchase.plan_name || selectedPurchase.remark}</Typography>
                <Chip label={getStatusMeta(selectedPurchase.status).label} size="small" sx={{ bgcolor: getStatusMeta(selectedPurchase.status).bg, color: getStatusMeta(selectedPurchase.status).color, fontWeight: 'bold' }} />
              </Box>
              <Grid container spacing={2} mb={4}>
                <Grid item xs={6}><Box sx={{ bgcolor: '#EFF6FF', borderRadius: 3, p: 2, textAlign: 'center' }}><MonetizationOn sx={{ color: '#3B82F6', mb: 1 }} /><Typography variant="h5" fontWeight="900">{selectedPurchase.amount}</Typography><Typography variant="caption">Purchased</Typography></Box></Grid>
                <Grid item xs={6}><Box sx={{ bgcolor: '#ECFDF5', borderRadius: 3, p: 2, textAlign: 'center' }}><CardGiftcard sx={{ color: '#10B981', mb: 1 }} /><Typography variant="h5" fontWeight="900">{selectedPurchase.bonus_credits || 0}</Typography><Typography variant="caption">Bonus</Typography></Box></Grid>
              </Grid>
              <Stack spacing={2} divider={<Divider />}>
                <Box display="flex" justifyContent="space-between"><Typography variant="body2" color="#64748B">Amount Paid</Typography><Typography variant="body1" fontWeight="800">₹{selectedPurchase.amount_paid || selectedPurchase.amount}</Typography></Box>
                <Box display="flex" justifyContent="space-between"><Typography variant="body2" color="#64748B">Reference ID</Typography><Typography variant="body2" fontWeight="700">{selectedPurchase.reference_id || 'N/A'}</Typography></Box>
                <Box display="flex" justifyContent="space-between"><Typography variant="body2" color="#64748B">Purchase Date</Typography><Typography variant="body2" fontWeight="700">{formatDate(selectedPurchase.created_at, 'full')}</Typography></Box>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}